import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Calendar, BookOpen, Award, Bell, Search, Building2,
  FileText,  Clock, Home, BookCheck,
  GraduationCap, BarChart3, ChevronRight,
  Bookmark, Users2, PlayCircle, AlertCircle,
  Loader2, Menu, LogOut, HelpCircle,
  ChevronLeft, Zap, X, 
  Sun, Trophy, PartyPopper, Info, ClipboardList,
  History, RefreshCw, Eye
} from 'lucide-react';
import api from '../api';
import logoutManager from '../logout';
import moment from 'moment';

// Components
import FacultyProfileModal from './components/FacultyProfileModal';
import ClassDetailsModal from './components/ClassDetailsModal';
import { useNavigate } from 'react-router-dom';
import TimetableModal from './components/TimetableModal';
import UpcomingClassesModal from './components/UpcomingClassesModal';
import StatCard from './components/StatCard';
import QuickActionCard from './components/QuickActionCard';
import UpcomingClassCard from './components/UpcomingClassCard';
import CompletedSessionsModal from './components/CompletedSessionsModal';
import AttendanceModal from './components/FacultyAttendanceModal';
import AttendanceHistoryModal from './components/AttendanceHistoryModal';

const AttendanceAlert = React.memo(({ unmarkedSessions, policy, onMarkNow }) => {
  const [markableSessions, setMarkableSessions] = React.useState([]);
  const sessionCacheRef = useRef(new Map());

  const candidateSignature = useMemo(() => {
    const ids = [
      ...(unmarkedSessions.today || []),
      ...(unmarkedSessions.past || [])
    ].map(session => `${session._id}:${session.sessionStatus?.status || session.attendanceMarkingStatus || ''}`);
    return ids.join('|');
  }, [unmarkedSessions.today, unmarkedSessions.past]);

  useEffect(() => {
    let isMounted = true;

    const fetchEligibility = async () => {
      const allCandidates = [
        ...(unmarkedSessions.today || []),
        ...(unmarkedSessions.past || [])
      ];
      const candidates = allCandidates.filter(s => {
        const status = s.sessionStatus?.status ?? s.status;
        return status !== 'completed';
      });

      const results = [];
      const now = new Date();

      for (const s of candidates) {
        try {
          const cached = sessionCacheRef.current.get(s._id);
          const sessionData = cached || (s.attendance ? s : null) || (await api.get(`/api/faculty/attendance/session/${s._id}`)).data?.data?.session || s;
          sessionCacheRef.current.set(s._id, sessionData);
          const canMark = sessionData?.sessionStatus?.canMark ?? sessionData?.canMark;
          const canEdit = sessionData?.sessionStatus?.canEdit ?? sessionData?.canEdit;
          const isUnblockedByAdmin = sessionData?.isUnblockedByAdmin;

          let showMark = false;
          const sessionStart = sessionData.startTime;
          const sessionDate = sessionData.sessionDate;
          let sessionStartDateTime = null;

          if (sessionStart && sessionDate) {
            sessionStartDateTime = new Date(`${new Date(sessionDate).toISOString().split('T')[0]}T${sessionStart}`);
          }

          if (sessionStartDateTime && now >= sessionStartDateTime) {
            showMark = true;
          }

          if (isUnblockedByAdmin === true) showMark = true;

          if ((canMark === true || canEdit === true || isUnblockedByAdmin === true) && showMark) {
            results.push(sessionData);
          }
        } catch (err) {
          // Ignore fetch errors for individual sessions
        }
      }

      if (isMounted) setMarkableSessions(results);
    };

    fetchEligibility();

    return () => {
      isMounted = false;
    };
  }, [candidateSignature, unmarkedSessions.today, unmarkedSessions.past]);

  const todayPending = markableSessions.length;

  if (todayPending === 0) return null;

  return (
    <div className="mb-6 rounded-2xl border border-amber-100/80 bg-gradient-to-r from-amber-50/85 via-orange-50/70 to-white p-4 shadow-sm shadow-amber-100/40 ring-1 ring-white/60 transition-all duration-300 ease-out hover:shadow-md hover:shadow-amber-100/50">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-white/80 ring-1 ring-amber-100/70 shadow-sm shadow-amber-100/40">
            <ClipboardList className="w-5 h-5 text-amber-500" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-amber-900">Pending Attendance</p>
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                {todayPending} alert{todayPending !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="mt-1 text-sm text-amber-700/90">
              You have {todayPending} class{todayPending !== 1 ? 'es' : ''} waiting for attendance marking.
            </p>
            {policy && (
              <p className="mt-1 text-xs text-amber-600/80">
                Policy: mark within {policy.blockAfterDays} days · edit window {policy.modificationWindowMinutes} min
              </p>
            )}
          </div>
        </div>
        <button 
          onClick={() => {
            const firstPending = markableSessions[0];
            if (firstPending) onMarkNow(firstPending);
          }}
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-sm shadow-amber-200/70 transition-all duration-300 ease-out hover:bg-amber-600 hover:shadow-md hover:shadow-amber-200/80 active:scale-[0.98]"
        >
          <ClipboardList className="w-4 h-4" />
          Mark Now
        </button>
      </div>
    </div>
  );
});

const FacultyDashboard = () => {
  const navigate = useNavigate();
    // Track last fetched userId to avoid redundant fetches
    const lastFetchedUserId = useRef(null);
  // State
  const [todaysEvents, setTodaysEvents] = useState([]);
  const [attendanceSessions, setAttendanceSessions] = useState({ upcoming: [], ongoing: [], completed: [] });
  const [unmarkedSessions, setUnmarkedSessions] = useState({ today: [], past: [] });

  // In-memory caches for fetched data
  const attendanceSessionsCache = useRef({}); // key: userId, value: sessions
  const unmarkedSessionsCache = useRef({}); // key: userId, value: sessions
  const todaysEventsCache = useRef({}); // key: userId, value: events
  const todayUpcomingClassesCache = useRef({}); // key: userId, value: classes
  const todayScheduleCache = useRef({}); // key: userId, value: schedule
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showAttendanceHistory, setShowAttendanceHistory] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [facultyData, setFacultyData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [, setTodayUpcomingClasses] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [lastClassModalState, setLastClassModalState] = useState(null);
  const [showTimetableModal, setShowTimetableModal] = useState(false);
  const [showUpcomingModal, setShowUpcomingModal] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [academicYear, ] = useState(null);
  const [attendanceModalOpenedFrom, setAttendanceModalOpenedFrom] = useState(null);
  const [stats, setStats] = useState({
    totalClasses: 0,
    upcomingSessions: 0,
    totalDepartments: 0,
    totalSubjects: 0,
    pendingAttendance: 0,
    editableSessions: 0
  });

  // Refs to prevent infinite loops
  const isMounted = useRef(true);
  const initialLoadDone = useRef(false);

  // Format time until
  const formatTimeUntil = useCallback((classTime) => {
    const now = new Date();
    const diffMs = classTime - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 0) return `Started ${Math.abs(diffMins)}m ago`;
    if (diffMins < 60) return `in ${diffMins}m`;
    if (diffHours < 24) return `in ${diffHours}h`;
    return `in ${Math.floor(diffHours / 24)}d`;
  }, []);

  const buildScheduleRoomKey = useCallback((sessionDate, startTime, endTime, classRef, subjectRef) => {
    const normalizeTime = (value) => {
      if (!value) return '';
      const parsed = moment(value, ['HH:mm', 'H:mm', 'hh:mm A', 'h:mm A'], true);
      return parsed.isValid() ? parsed.format('HH:mm') : String(value).trim();
    };

    return [
      sessionDate ? moment(sessionDate).format('YYYY-MM-DD') : '',
      normalizeTime(startTime),
      normalizeTime(endTime),
      classRef || '',
      subjectRef || ''
    ].join('|');
  }, []);

  const mergeScheduleRoomData = useCallback((attendanceSchedule, timetableClasses) => {
    const roomMap = new Map();

    const timetableEntries = (timetableClasses || []).flatMap(day => {
      const dayDateISO = day?.dateISO || day?.date || null;
      if (Array.isArray(day?.entries)) {
        return day.entries.map(entry => ({ ...entry, _dayDateISO: dayDateISO }));
      }
      if (Array.isArray(day)) return day;
      return [];
    });

    timetableEntries.forEach(item => {
      const classRef = item.classId?._id || item.classId || item.className || item.classCode;
      const subjectRef = item.subjectId?._id || item.subjectId || item.subjectName || item.subjectCode;
      const key = buildScheduleRoomKey(
        item.sessionDate || item._dayDateISO || item.date,
        item.startTime || item.timeSlot?.start || item.timeSlot?.originalStart,
        item.endTime || item.timeSlot?.end || item.timeSlot?.originalEnd,
        classRef,
        subjectRef
      );
      if (key) roomMap.set(key, item.room || item.roomName || item.classRoom || item.classroom || item.location?.name || '');
    });

    return (attendanceSchedule || []).map(item => {
      const classRef = item.classId?._id || item.classId || item.className || item.classCode;
      const subjectRef = item.subjectId?._id || item.subjectId || item.subjectSnapshot?.subjectName || item.subjectId?.name || item.subjectCode;
      const key = buildScheduleRoomKey(
        item.sessionDate,
        item.startTime || item.timeSlot?.start,
        item.endTime || item.timeSlot?.end,
        classRef,
        subjectRef
      );
      return {
        ...item,
        room: item.room || roomMap.get(key) || item.roomName || item.classRoom || item.classroom || ''
      };
    });
  }, [buildScheduleRoomKey]);

  // Calculate today's classes from week data
  const calculateTodaysClassesFromWeek = useCallback((weekData) => {
    if (!weekData || !Array.isArray(weekData)) return [];
    const today = new Date().toISOString().split('T')[0];
    const now = new Date();
    const todaysClasses = [];
    const todayData = weekData.find(day => day.dateISO === today);
    
    if (!todayData || todayData.hasHidingEvents) return [];

    let entries = todayData.entries || [];
    if (entries.some(e => e.isOverride)) {
      entries = entries.filter(e => e.isOverride);
    }

    if (entries.length > 0) {
      entries.forEach(entry => {
        if (entry.isEvent || entry.isBreak || !entry.isTeachingSession) return;
        if (entry.timeSlot && entry.timeSlot.start) {
          const classTime = new Date(`${today}T${entry.timeSlot.originalStart || entry.timeSlot.start}`);
          const timeDiff = (classTime - now) / (1000 * 60);
          
          let timeStatus = 'upcoming';
          if (timeDiff <= 0 && timeDiff >= -120) timeStatus = 'in_progress';
          else if (timeDiff <= 30 && timeDiff > 0) timeStatus = 'starting_soon';
          else if (timeDiff > 30) timeStatus = 'upcoming';
          else return;

          todaysClasses.push({
            ...entry,
            date: today,
            dayName: todayData.dayName,
            classTime: classTime.toISOString(),
            isToday: true,
            timeUntil: formatTimeUntil(classTime),
            displayDate: 'Today',
            timeStatus,
            minutesUntil: timeDiff
          });
        }
      });
    }
    return todaysClasses.sort((a, b) => new Date(a.classTime) - new Date(b.classTime));
  }, [formatTimeUntil]);

  const getAttendanceSessionFlags = useCallback((session) => {
    const rawStatus = String(session?.sessionStatus?.status || session?.attendanceMarkingStatus || '').toLowerCase();
    const isMarked =
      rawStatus === 'marked' ||
      rawStatus === 'marked-editable' ||
      rawStatus === 'marked-locked' ||
      rawStatus === 'completed' ||
      session?.hasAttendanceMarked === true;
    const isBlocked = rawStatus === 'locked' || session?.isLocked === true || session?.isBlocked === true;
    const isEditable = rawStatus === 'marked-editable' || Boolean(session?.sessionStatus?.canEdit);
    const attendancePercentage = Number(
      session?.statistics?.attendancePercentage ?? session?.attendancePercentage ?? 0
    );

    return { rawStatus, isMarked, isBlocked, isEditable, attendancePercentage };
  }, []);

  // Fetch attendance policy
  const fetchPolicy = useCallback(async () => {
    try {
      console.log('[FacultyDashboard][fetchPolicy] Requesting /api/grading-attendance-rules');
      const response = await api.get(`/api/grading-attendance-rules`);
      console.log('[FacultyDashboard][fetchPolicy] Response:', response);
      if (response.data.success && isMounted.current) {
        setPolicy(response.data.data?.attendancePolicy);
      }
    } catch (error) {
      console.error('Error fetching policy:', error, error?.response);
    }
  }, []);

  // Fetch today's events with cache
  const fetchTodaysEvents = useCallback(async (userId) => {
    if (todaysEventsCache.current[userId]) {
      setTodaysEvents(todaysEventsCache.current[userId]);
      return todaysEventsCache.current[userId];
    }
    try {
      console.log('[FacultyDashboard][fetchTodaysEvents] Requesting /api/faculty/' + userId + '/timetable/personal');
      const response = await api.get(`/api/faculty/${userId}/timetable/personal`, {
        params: {
          weekStartDate: new Date().toISOString().split('T')[0],
          viewMode: 'custom'
        }
      });
      console.log('[FacultyDashboard][fetchTodaysEvents] Response:', response);
      if (response.data.success && isMounted.current) {
        const weekData = response.data.data.week || [];
        const today = new Date().toISOString().split('T')[0];
        const todayData = weekData.find(day => day.dateISO === today);
        if (!todayData) return [];
        const events = (todayData.entries || []).filter(entry => entry.isEvent && entry.eventType);
        setTodaysEvents(events);
        todaysEventsCache.current[userId] = events;
        return events;
      }
      return [];
    } catch (error) {
      console.error('Error fetching events:', error, error?.response);
      return [];
    }
  }, []);

  // Fetch attendance sessions with cache
  const fetchAttendanceSessions = useCallback(async (userId) => {
    if (attendanceSessionsCache.current[userId]) {
      setAttendanceSessions(attendanceSessionsCache.current[userId]);
      return attendanceSessionsCache.current[userId];
    }
    try {
      console.log('[FacultyDashboard][fetchAttendanceSessions] Requesting /api/faculty/' + userId + '/attendance/upcoming');
      const response = await api.get(`/api/faculty/${userId}/attendance/upcoming`);
      console.log('[FacultyDashboard][fetchAttendanceSessions] Response:', response);
      if (response.data.success && isMounted.current) {
        setAttendanceSessions(response.data.data);
        attendanceSessionsCache.current[userId] = response.data.data;
        return response.data.data;
      }
      return { upcoming: [], ongoing: [], completed: [] };
    } catch (error) {
      console.error('Error fetching attendance sessions:', error, error?.response);
      return { upcoming: [], ongoing: [], completed: [] };
    }
  }, []);

  // Fetch unmarked sessions with cache
  const fetchUnmarkedSessions = useCallback(async (userId) => {
    if (unmarkedSessionsCache.current[userId]) {
      setUnmarkedSessions(unmarkedSessionsCache.current[userId]);
      return unmarkedSessionsCache.current[userId];
    }
    try {
      console.log('[FacultyDashboard][fetchUnmarkedSessions] Requesting /api/faculty/' + userId + '/attendance/unmarked');
      const response = await api.get(`/api/faculty/${userId}/attendance/unmarked`);
      console.log('[FacultyDashboard][fetchUnmarkedSessions] Response:', response);
      if (response.data.success && isMounted.current) {
        const sessions = {
          today: response.data.data.today || [],
          past: response.data.data.past || []
        };
        setUnmarkedSessions(sessions);
        unmarkedSessionsCache.current[userId] = sessions;
        return sessions;
      }
      return { today: [], past: [] };
    } catch (error) {
      console.error('Error fetching unmarked sessions:', error, error?.response);
      return { today: [], past: [] };
    }
  }, []);

  // Fetch attendance summary with cache
  const fetchAttendanceSummary = useCallback(async (userId) => {
    try {
      console.log('[FacultyDashboard][fetchAttendanceSummary] Requesting /api/faculty/' + userId + '/attendance/all-with-status');
      const response = await api.get(`/api/faculty/${userId}/attendance/all-with-status`);
      console.log('[FacultyDashboard][fetchAttendanceSummary] Response:', response);
      if (response.data.success && isMounted.current) {
        const summary = response.data.data?.summary || null;
        setAttendanceSummary(summary);
        return summary;
      }
      return null;
    } catch (error) {
      console.error('Error fetching attendance summary:', error, error?.response);
      return null;
    }
  }, []);

  // Fetch today's classes with cache
  const fetchTodaysClasses = useCallback(async (userId) => {
    if (todayUpcomingClassesCache.current[userId]) {
      setTodayUpcomingClasses(todayUpcomingClassesCache.current[userId]);
      return todayUpcomingClassesCache.current[userId];
    }
    try {
      console.log('[FacultyDashboard][fetchTodaysClasses] Requesting /api/faculty/' + userId + '/timetable/personal');
      const response = await api.get(`/api/faculty/${userId}/timetable/personal`, {
        params: {
          weekStartDate: new Date().toISOString().split('T')[0],
          viewMode: 'custom'
        }
      });
      console.log('[FacultyDashboard][fetchTodaysClasses] Response:', response);
      if (response.data.success && isMounted.current) {
        const weekData = response.data.data.week || [];
        const todaysClasses = calculateTodaysClassesFromWeek(weekData);
        setTodayUpcomingClasses(todaysClasses);
        todayUpcomingClassesCache.current[userId] = todaysClasses;
        return todaysClasses;
      }
      return [];
    } catch (error) {
      console.error('Error fetching today\'s classes:', error, error?.response);
      return [];
    }
  }, [calculateTodaysClassesFromWeek]);

  // Fetch today's schedule with attendance status
  const fetchTodaySchedule = useCallback(async (userId) => {
    try {
      console.log('[FacultyDashboard][fetchTodaySchedule] Requesting attendance schedule + timetable room data for', userId);
      const [attendanceResponse, timetableResponse] = await Promise.all([
        api.get(`/api/faculty/${userId}/attendance/today-schedule`),
        api.get(`/api/faculty/${encodeURIComponent(userId)}/timetable/personal`, {
          params: {
            weekStartDate: new Date().toISOString().split('T')[0],
            viewMode: 'custom'
          }
        })
      ]);

      if (attendanceResponse.data.success && isMounted.current) {
        const schedule = attendanceResponse.data.data?.schedule || [];
        const timetableClasses = timetableResponse.data?.success ? timetableResponse.data.data?.week || [] : [];
        const mergedSchedule = mergeScheduleRoomData(schedule, timetableClasses);
        setTodaySchedule(mergedSchedule);
        todayScheduleCache.current[userId] = mergedSchedule;
        return mergedSchedule;
      }
      return [];
    } catch (error) {
      console.error('Error fetching today schedule:', error, error?.response);
      return [];
    }
  }, [mergeScheduleRoomData]);

  // Refresh all data
  const refreshAllData = useCallback(async () => {
    setRefreshing(true);
    const userId = localStorage.getItem('userId');
    if (userId && isMounted.current) {
      await Promise.all([
        fetchAttendanceSessions(userId),
        fetchUnmarkedSessions(userId),
        fetchTodaysClasses(userId),
        fetchTodaySchedule(userId),
        fetchTodaysEvents(userId)
      ]);
    }
    if (isMounted.current) setRefreshing(false);
  }, [fetchAttendanceSessions, fetchUnmarkedSessions, fetchTodaysClasses, fetchTodaySchedule, fetchTodaysEvents]);

  // Fetch dashboard data - wrapped with useCallback and proper dependencies
  const fetchDashboardData = useCallback(async () => {
    try {
      if (!isMounted.current) return;
      setLoading(true);
      setError(null);
      const userId = localStorage.getItem('userId');
      if (!userId) throw new Error('User ID not found. Please login again.');

      // Fetch all data in parallel
      console.log('[FacultyDashboard][refreshAllData] Fetching all dashboard data for user:', userId);
      await Promise.all([
        fetchPolicy(),
        fetchAttendanceSessions(userId),
        fetchUnmarkedSessions(userId),
        fetchAttendanceSummary(userId),
        fetchTodaysClasses(userId),
        fetchTodaySchedule(userId),
        fetchTodaysEvents(userId)
      ]);

      console.log('[FacultyDashboard][refreshAllData] Fetching /api/faculty/dashboard/' + userId);
      const dashboardRes = await api.get(`/api/faculty/dashboard/${userId}`);
      console.log('[FacultyDashboard][refreshAllData] Dashboard response:', dashboardRes);
      if (dashboardRes.data.success && isMounted.current) {
        const data = dashboardRes.data.data;
        setFacultyData(data.facultyInfo);
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error, error?.response);
      if (isMounted.current) setError(error.message || 'Failed to load data');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [fetchPolicy, fetchAttendanceSessions, fetchUnmarkedSessions, fetchAttendanceSummary, fetchTodaysClasses, fetchTodaySchedule, fetchTodaysEvents]);

  // Update stats when dependencies change
  useEffect(() => {
    if (!dashboardData) return;
    
    const localDepartments = Array.isArray(dashboardData.departments) && dashboardData.departments.length
      ? dashboardData.departments
      : (() => {
          const classes = dashboardData.classes || dashboardData.classList || [];
          const map = new Map();
          (classes || []).forEach(c => {
            if (c.department && c.department._id) {
              if (!map.has(c.department._id)) map.set(c.department._id, { _id: c.department._id, name: c.department.name });
            } else if (c.departmentId) {
              const id = c.departmentId.toString ? c.departmentId.toString() : c.departmentId;
              if (!map.has(id)) map.set(id, { _id: id, name: c.departmentName || 'Unknown Dept' });
            }
          });
          return Array.from(map.values());
        })();
    
    const summaryMarked = Number(attendanceSummary?.marked ?? 0);
    const summaryBlocked = Number(attendanceSummary?.blocked ?? 0);
    const summaryPending = Number(attendanceSummary?.unmarked ?? 0);
    const summaryAverage = Number(attendanceSummary?.averageAttendance ?? 0);

    const blockedCount = summaryBlocked || (
      (unmarkedSessions.today || []).filter(session => session.isLocked === true || session.sessionStatus?.status === 'locked').length +
      (unmarkedSessions.past || []).filter(session => session.isLocked === true || session.sessionStatus?.status === 'locked').length
    );

    const pendingCount = summaryPending || (
      (unmarkedSessions.today || []).filter(session => {
        const flags = getAttendanceSessionFlags(session);
        return !flags.isBlocked && !flags.isMarked;
      }).length +
      (unmarkedSessions.past || []).filter(session => {
        const flags = getAttendanceSessionFlags(session);
        return !flags.isBlocked && !flags.isMarked;
      }).length
    );

    const completedSessions = attendanceSessions.completed || [];
    const markedSessions = completedSessions.filter(session => getAttendanceSessionFlags(session).isMarked);
    const editableSessions = completedSessions.filter(session => getAttendanceSessionFlags(session).isEditable);
    const overallAttendance = summaryAverage || (markedSessions.length
      ? Math.round(
          markedSessions.reduce((sum, session) => sum + getAttendanceSessionFlags(session).attendancePercentage, 0) /
          markedSessions.length
        )
      : 0);
    setStats({
      totalClasses: dashboardData.summary?.totalClasses || 0,
      upcomingSessions: todaySchedule.length || 0,
      totalDepartments: localDepartments.length || dashboardData.summary?.totalDepartments || 0,
      totalSubjects: dashboardData.summary?.totalSubjects || 0,
      pendingAttendance: pendingCount,
      blockedAttendance: blockedCount,
      markedAttendance: summaryMarked || markedSessions.length,
      editableSessions: editableSessions.length,
      overallAttendance
    });
  }, [dashboardData, todaySchedule.length, unmarkedSessions.today, unmarkedSessions.past, attendanceSessions.completed, attendanceSummary, getAttendanceSessionFlags]);

  // Initial load - only once, or when userId changes
  useEffect(() => {
    isMounted.current = true;
    const userId = localStorage.getItem('userId');
    if (!initialLoadDone.current || lastFetchedUserId.current !== userId) {
      initialLoadDone.current = true;
      lastFetchedUserId.current = userId;
      fetchDashboardData();
    }
    return () => {
      isMounted.current = false;
    };
  }, [fetchDashboardData]);

  // Fetch completed sessions
  const fetchCompletedSessions = useCallback(async (userId) => {
    try {
      const response = await api.get(`/api/faculty/${userId}/attendance/history`, { params: { limit: 50 } });
      if (response.data.success && isMounted.current) {
        setCompletedSessions(response.data.data.sessions);
      }
    } catch (error) {
      console.error('Error fetching completed sessions:', error);
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (userId) fetchCompletedSessions(userId);
  }, [fetchCompletedSessions]);

  // Handlers

  // Called when a class is clicked from the dashboard
  const handleClassClick = (classData) => {
    setShowClassModal(true);
    setLastClassModalState(null); // Reset any previous modal state
  };

  // Callback to reopen the class modal after subject details closes
  const handleReopenClassModal = ({ classItem, subject, labBatch }) => {
    setShowClassModal(true);
    setLastClassModalState({ classItem, subject, labBatch });
  };


  // Always fetch session details by ID before opening modal for marking/editing
  const handleEditAttendance = async (session) => {
    try {
      if (session?.attendance && Array.isArray(session.attendance)) {
        setShowAttendanceHistory(false);
        setSelectedSession(session);
        setTimeout(() => setShowAttendanceModal(true), 100);
        return;
      }
      console.log('[Mark Clicked][Summary Session]', session);
      const response = await api.get(`/api/faculty/attendance/session/${session._id}`);
      const sessionData = response.data?.data?.session || session;
      console.log('[Mark Clicked][Fetched Session Detail]', sessionData);
      // Explicitly log eligibility fields for debugging
      const canEdit = sessionData?.sessionStatus?.canEdit ?? sessionData?.canEdit;
      const canMark = sessionData?.sessionStatus?.canMark ?? sessionData?.canMark;
      const isUnblockedByAdmin = sessionData?.isUnblockedByAdmin;
      console.log('[Mark Clicked][Eligibility Flags]', {
        canMark,
        canEdit,
        isUnblockedByAdmin,
        sessionStatus: sessionData?.sessionStatus,
        topLevel: {
          canMark: sessionData?.canMark,
          canEdit: sessionData?.canEdit
        }
      });
      if (canEdit === true || isUnblockedByAdmin === true || canMark === true) {
        setShowAttendanceHistory(false);
        setSelectedSession(sessionData);
        setTimeout(() => setShowAttendanceModal(true), 100);
      } else {
        alert('Marking is blocked for this session by policy.');
      }
    } catch (err) {
      alert('Failed to fetch session details. Please try again.');
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logoutManager.logout({
      redirectTo: '/',
      showToast: true,
      callbacks: true,
      force: false
    });
    setIsLoggingOut(false);
  };

  // Navigation Item Component
  const NavigationItem = ({ icon: Icon, label, isActive, onClick, badge, collapsed }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center px-4 py-2.5 mb-1 rounded-xl transition-all duration-200 group relative ${
        isActive 
          ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-lg shadow-indigo-500/25' 
          : 'text-slate-300 hover:bg-white/10 hover:text-white'
      } ${collapsed ? 'justify-center' : 'gap-3'}`}
      title={collapsed ? label : ''}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 transition-transform group-hover:scale-105 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} />
      {!collapsed && (
        <>
          <span className="font-medium text-sm">{label}</span>
          {badge > 0 && (
            <span className="ml-auto bg-white/20 text-white text-xs font-semibold px-2 py-0.5 rounded-full min-w-[20px]">
              {badge}
            </span>
          )}
        </>
      )}
      {collapsed && badge > 0 && (
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-400 rounded-full ring-2 ring-slate-800"></span>
      )}
    </button>
  );

  const quickActions = [
    { icon: FileText, label: 'Create Assignment', description: 'Post new task', color: 'blue', action: () => console.log('Create Assignment') },
    { icon: Award, label: 'Grade Papers', description: 'Review submissions', color: 'emerald', action: () => console.log('Grade Papers') },
    { icon: History, label: 'View Attendance', description: 'view and manage all attendance records', color: 'purple', action: () => setShowAttendanceHistory(true) }
  ];

  // Loading and Error States
  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Loading your dashboard...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Unable to Load Dashboard</h2>
        <p className="text-slate-500 mb-6">{error}</p>
        <button 
          onClick={() => {
            initialLoadDone.current = false;
            fetchDashboardData();
          }} 
          className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-indigo-600 transition-all"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 lg:pb-0">
      {/* Removed static pending attendance alert. Only dynamic AttendanceAlert will show. */}
      {/* Today's Events Card */}
      {todaysEvents.length > 0 && (
        <div className="max-w-3xl mx-auto mt-6 mb-4 px-4">
          {todaysEvents.map((event, idx) => (
            <div key={idx} className={`flex items-center gap-4 p-5 rounded-2xl shadow-lg border-l-8 mb-3
              ${event.eventType === 'holiday' ? 'bg-gradient-to-r from-emerald-50 to-white border-emerald-400' :
                event.eventType === 'exam' ? 'bg-gradient-to-r from-amber-50 to-white border-amber-400' :
                'bg-gradient-to-r from-indigo-50 to-white border-indigo-400'}
            `}>
              <div className="flex-shrink-0">
                {event.eventType === 'holiday' && <Sun className="w-8 h-8 text-emerald-500" />}
                {event.eventType === 'exam' && <Trophy className="w-8 h-8 text-amber-500" />}
                {event.eventType === 'academic_event' && <PartyPopper className="w-8 h-8 text-indigo-500" />}
              </div>
              <div>
                <div className="font-bold text-lg text-slate-800">
                  {event.eventTitle || event.title || 'Event'}
                  {event.eventType === 'holiday' && <span className="ml-2 px-2 py-0.5 rounded bg-emerald-100 text-emerald-600 text-xs font-semibold">Holiday</span>}
                  {event.eventType === 'exam' && <span className="ml-2 px-2 py-0.5 rounded bg-amber-100 text-amber-600 text-xs font-semibold">Exam</span>}
                </div>
                {event.eventDescription && <div className="text-slate-600 mt-1 text-sm">{event.eventDescription}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Refresh Button - Adjusted for safe mobile tapping workspace area */}
      <div className="fixed bottom-24 right-6 z-40 lg:bottom-6">
        <button
          onClick={refreshAllData}
          disabled={refreshing}
          className="p-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-all disabled:opacity-50"
          title="Refresh Data"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar - Always Expanding on Hover Pattern on Desktop */}
      <aside 
        onMouseEnter={() => setSidebarCollapsed(false)}
        onMouseLeave={() => setSidebarCollapsed(true)}
        className={`fixed inset-y-0 left-0 z-50 bg-gradient-to-b from-slate-900 to-slate-800 transition-all duration-300 flex flex-col shadow-2xl ${sidebarCollapsed ? 'w-20' : 'w-72'} ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className="h-20 flex items-center justify-between px-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-lg">
              <GraduationCap className="w-5 h-5" />
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-white font-bold text-lg tracking-tight">Faculty<span className="text-indigo-400">Portal</span></h1>
                <p className="text-white/50 text-[10px] -mt-0.5">Academic Management</p>
              </div>
            )}
          </div>
          <button 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="text-white/50 hover:text-white transition-colors hidden lg:block"
          >
            {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 overflow-y-auto">
          <div className="space-y-1">
            <p className={`px-3 text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2 ${sidebarCollapsed ? 'text-center' : ''}`}>
              {sidebarCollapsed ? '📚' : 'TEACHING'}
            </p>
            <NavigationItem icon={Home} label="Overview" isActive={true} onClick={() => {}} collapsed={sidebarCollapsed} />
            <NavigationItem icon={Clock} label="Daily Schedule" badge={stats.upcomingSessions} onClick={() => setShowUpcomingModal(true)} collapsed={sidebarCollapsed} />
            <NavigationItem icon={BookCheck} label="My Classes" onClick={() => setShowClassModal(true)} collapsed={sidebarCollapsed} />
            <NavigationItem icon={Calendar} label="Timetable" onClick={() => setShowTimetableModal(true)} collapsed={sidebarCollapsed} />
          </div>
          
          <div className="my-6 border-t border-white/10"></div>
          
          <div className="space-y-1">
            <p className={`px-3 text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2 ${sidebarCollapsed ? 'text-center' : ''}`}>
              {sidebarCollapsed ? '⚙️' : 'ADMINISTRATION'}
            </p>
            <NavigationItem icon={FileText} label="Assignments" collapsed={sidebarCollapsed} />
            <NavigationItem icon={Award} label="Grading" collapsed={sidebarCollapsed} />
            <NavigationItem icon={ClipboardList} label="Attendance" badge={stats.pendingAttendance} onClick={() => setShowAttendanceHistory(true)} collapsed={sidebarCollapsed} />
            <NavigationItem icon={BarChart3} label="Reports" collapsed={sidebarCollapsed} />
          </div>
        </nav>

        <div className="p-4 border-t border-white/10 bg-white/5">
          <button onClick={() => setShowProfileModal(true)} className="flex items-center w-full group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-md group-hover:shadow-lg transition-all">
              {facultyData?.name?.charAt(0) || 'F'}
            </div>
            {!sidebarCollapsed && (
              <div className="ml-3 flex-1 text-left">
                <p className="text-sm font-medium text-white truncate">{facultyData?.name || 'Faculty Name'}</p>
                <p className="text-[11px] text-white/50 truncate">View Profile</p>
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'} ${mobileMenuOpen ? 'overflow-hidden' : ''}`}>
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="px-4 lg:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 hover:bg-slate-100 rounded-xl text-slate-600">
                <Menu className="w-5 h-5" />
              </button>
              <div className="hidden lg:flex items-center gap-2 text-sm">
                <span className="text-slate-500">Faculty Portal</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
                <span className="font-semibold text-slate-800">Dashboard</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden md:block relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" placeholder="Search students, classes..." className="pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm w-80 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <button className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white"></span>
              </button>
              <button className="p-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors md:hidden">
                <Search className="w-5 h-5" />
              </button>
              <button onClick={() => setShowLogoutModal(true)} className="ml-2 p-2 rounded-xl text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          {/* Logout Modal */}
          {showLogoutModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center relative">
                <button className="absolute top-3 right-3 text-slate-400 hover:text-slate-600" onClick={() => setShowLogoutModal(false)}>
                  <X className="w-5 h-5" />
                </button>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogOut className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Confirm Logout</h2>
                <p className="text-slate-500 mb-6">Are you sure you want to logout?</p>
                <div className="flex gap-3 justify-center">
                  <button onClick={() => setShowLogoutModal(false)} className="px-5 py-2 rounded-xl bg-slate-100 text-slate-600 font-medium hover:bg-slate-200 transition-all">Cancel</button>
                  <button onClick={handleLogout} className="px-5 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-medium shadow hover:from-red-600 hover:to-red-700 transition-all">
                    {isLoggingOut ? 'Logging out...' : 'Logout'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <AttendanceAlert
            unmarkedSessions={unmarkedSessions}
            policy={policy}
            onMarkNow={handleEditAttendance}
          />
          {/* <EditableSessionsAlert /> */}
          {/* <PastUnmarkedSessions /> */}
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* === REDESIGN ELEMENT: DASHBOARD NAVIGATION HUB CARD === */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-indigo-500" /> Academic Command Center
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Action 1: My Classes */}
                  <button 
                    onClick={() => setShowClassModal(true)} 
                    className="flex flex-col items-start p-4 bg-gradient-to-br from-indigo-50 to-white hover:from-indigo-100/70 border border-indigo-100 rounded-xl text-left transition-all group shadow-sm hover:shadow"
                  >
                    <div className="p-2.5 bg-indigo-600 rounded-xl text-white mb-3 group-hover:scale-105 transition-transform">
                      <BookCheck className="w-5 h-5" />
                    </div>
                    <span className="font-bold text-slate-800 text-base">My Classes</span>
                    <span className="text-xs text-slate-500 mt-1">View active student batches, subjects & management structures</span>
                  </button>

                  {/* Action 2: Timetable */}
                  <button 
                    onClick={() => setShowTimetableModal(true)} 
                    className="flex flex-col items-start p-4 bg-gradient-to-br from-teal-50 to-white hover:from-teal-100/70 border border-teal-100 rounded-xl text-left transition-all group shadow-sm hover:shadow"
                  >
                    <div className="p-2.5 bg-teal-600 rounded-xl text-white mb-3 group-hover:scale-105 transition-transform">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <span className="font-bold text-slate-800 text-base">Master Timetable</span>
                    <span className="text-xs text-slate-500 mt-1">Check full comprehensive weekly structural hour mapping grids</span>
                  </button>

                  {/* Action 3: Daily Schedule */}
                  <button 
                    onClick={() => setShowUpcomingModal(true)} 
                    className="flex flex-col items-start p-4 bg-gradient-to-br from-amber-50 to-white hover:from-amber-100/70 border border-amber-100 rounded-xl text-left transition-all group shadow-sm hover:shadow"
                  >
                    <div className="p-2.5 bg-amber-600 rounded-xl text-white mb-3 group-hover:scale-105 transition-transform">
                      <Clock className="w-5 h-5" />
                    </div>
                    <span className="font-bold text-slate-800 text-base">Daily Schedule</span>
                    <span className="text-xs text-slate-500 mt-1">Track all structured track hours, completed blocks & metrics</span>
                  </button>
                </div>
              </div>

              {/* Ongoing Attendance Sessions */}
              {attendanceSessions.ongoing?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <PlayCircle className="w-5 h-5 text-green-600" />
                      Live / Ongoing Sessions
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {attendanceSessions.ongoing.map((session, idx) => (
                      <div key={idx} className="bg-gradient-to-r from-green-50 to-white rounded-xl p-4 border border-green-200 shadow-sm">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-slate-800">{session.subjectSnapshot?.subjectName || session.subjectId?.name}</h4>
                              {/* Only show LIVE if session is not completed and has time left */}
                              {session.minutesLeft !== null && session.minutesLeft > 0 && !session.hasAttendanceMarked && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 animate-pulse">LIVE</span>
                              )}
                              {session.minutesLeft !== null && session.minutesLeft > 0 && (
                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{session.minutesLeft} min left</span>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mt-1">{session.className}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                              <span>🕐 {session.startTime ? moment(session.startTime, 'HH:mm').format('hh:mm A') : ''} - {session.endTime ? moment(session.endTime, 'HH:mm').format('hh:mm A') : ''}</span>
                              <span>👥 {session.statistics?.totalStudents || 0} students</span>
                            </div>
                          </div>
                          <button onClick={() => handleEditAttendance(session)} className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl font-medium hover:from-green-700 hover:to-green-600 transition-all shadow-md flex items-center gap-2">
                            <ClipboardList className="w-4 h-4" />
                            Mark Attendance Now
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Today's Schedule */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-indigo-600" />
                    Today's Schedule
                  </h3>
                  {todaySchedule.length > 0 && (
                    <button onClick={() => setShowUpcomingModal(true)} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                      View all <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {todaySchedule.length > 0 ? (
                  <div className="space-y-3">
                    {todaySchedule.slice(0, 3).map((item, idx) => (
                      <UpcomingClassCard key={item._id || idx} classItem={item} onClick={() => handleClassClick(item)} />
                    ))}
                    {todaySchedule.length > 3 && (
                      <button onClick={() => setShowUpcomingModal(true)} className="w-full py-3 text-center text-slate-500 text-sm hover:text-indigo-600 transition-colors">
                        + {todaySchedule.length - 3} more class{todaySchedule.length - 3 !== 1 ? 'es' : ''}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 text-center border border-dashed border-slate-200">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="text-slate-600 font-medium">No classes scheduled for today</p>
                    <p className="text-sm text-slate-400 mt-1">Perfect time to catch up on grading! 📝</p>
                  </div>
                )}
              </div>

              {/* Stats Overview */}
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Overview
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatCard title="Total Classes" value={stats.totalClasses} icon={BookOpen} color="indigo" />
                  <StatCard title="Departments" value={stats.totalDepartments} icon={Building2} color="slate" />
                  <StatCard title="Subjects" value={stats.totalSubjects} icon={Bookmark} color="teal" />
                  <StatCard title="Students" value="--" icon={Users2} color="amber" />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Quick Actions */}
              {console.log('DEBUG: unmarkedSessions.past', unmarkedSessions.past)}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4" /> Quick Actions
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {quickActions.map((action, idx) => (
                    <QuickActionCard key={idx} {...action} />
                  ))}
                </div>
              </div>

              {/* Attendance Summary */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" /> Attendance Summary
                  </h3>
                  <button onClick={() => setShowAttendanceHistory(true)} className="text-xs text-indigo-600 font-medium hover:underline flex items-center gap-1">
                    <Eye className="w-3 h-3" /> View History
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Marked</span>
                    <span className="font-semibold text-green-600">{stats.markedAttendance || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Blocked</span>
                    <span className="font-semibold text-red-600">{stats.blockedAttendance || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Pending</span>
                    <span className="font-semibold text-amber-600">{stats.pendingAttendance || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Upcoming</span>
                    <span className="font-semibold text-blue-600">{attendanceSessions.upcoming?.length || 0}</span>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Overall Attendance</span>
                      <span className="font-bold text-indigo-600">
                        {stats.overallAttendance}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Policy Info */}
              {policy && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Info className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-800 text-sm">Attendance Policy</h4>
                      <p className="text-xs text-blue-600 mt-1">
                        • Mark attendance within {policy.blockAfterDays} days<br />
                        • Edit window: {policy.modificationWindowMinutes} minutes<br />
                        • Minimum required: {policy.defaultMinimumPercentage}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Help Section */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Need Help?</h4>
                    <p className="text-xs text-slate-500 mt-1">Check documentation or contact support</p>
                    <button className="mt-3 text-xs font-medium text-indigo-600 hover:text-indigo-700">View Help Center →</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* === REDESIGN ELEMENT: MOBILE PERSISTENT BOTTOM NAVIGATION BAR === */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 py-2 px-4 flex justify-around items-center z-50 lg:hidden shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
          className="flex flex-col items-center justify-center gap-0.5 text-indigo-600"
        >
          <Home className="w-5 h-5" />
          <span className="text-[10px] font-bold">Home</span>
        </button>

        <button 
          onClick={() => setShowClassModal(true)} 
          className="flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:text-indigo-600 transition-colors"
        >
          <BookCheck className="w-5 h-5" />
          <span className="text-[10px] font-medium">My Classes</span>
        </button>

        <button 
          onClick={() => setShowTimetableModal(true)} 
          className="flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:text-indigo-600 transition-colors"
        >
          <Calendar className="w-5 h-5" />
          <span className="text-[10px] font-medium">Timetable</span>
        </button>

        <button 
          onClick={() => setShowAttendanceHistory(true)} 
          className="flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:text-indigo-600 transition-colors relative"
        >
          <ClipboardList className="w-5 h-5" />
          {stats.pendingAttendance > 0 && (
            <span className="absolute top-0 right-3 bg-red-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {stats.pendingAttendance}
            </span>
          )}
          <span className="text-[10px] font-medium">Attendance</span>
        </button>

        <button 
          onClick={() => setShowProfileModal(true)} 
          className="flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:text-indigo-600 transition-colors"
        >
          <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
            {facultyData?.name?.charAt(0) || 'F'}
          </div>
          <span className="text-[10px] font-medium">Profile</span>
        </button>
      </div>

      {/* Modals */}

      <ClassDetailsModal 
        isOpen={showClassModal} 
        onClose={() => setShowClassModal(false)} 
        facultyId={facultyData?.facultyId || localStorage.getItem('userId')} 
        academicYear={academicYear} 
        lastModalState={lastClassModalState}
        onOpenSubjectDetails={({ classItem, subject, labBatch, facultyId, classId, subjectId }) => {
          // Pass the callback in navigation state
          navigate('/faculty/class-subject-details', {
            state: {
              classItem,
              subject,
              labBatch,
              facultyId,
              classId,
              subjectId,
              onReopenClassModal: handleReopenClassModal
            }
          });
          setShowClassModal(false);
        }}
      />
      
      <TimetableModal 
        isOpen={showTimetableModal} 
        onClose={() => setShowTimetableModal(false)} 
        facultyId={facultyData?.facultyId || localStorage.getItem('userId')} 
      />
      
      <FacultyProfileModal 
        isOpen={showProfileModal} 
        onClose={() => setShowProfileModal(false)} 
        facultyId={facultyData?.facultyId || localStorage.getItem('userId')} 
      />
      
      <UpcomingClassesModal 
        isOpen={showUpcomingModal} 
        onClose={() => setShowUpcomingModal(false)} 
        facultyId={facultyData?.facultyId || localStorage.getItem('userId')} 
      />
      
      <CompletedSessionsModal 
        isOpen={showCompletedModal} 
        onClose={() => setShowCompletedModal(false)} 
        sessions={completedSessions} 
      />
      
      {/* Attendance Modal */}
      <AttendanceModal
        isOpen={showAttendanceModal}
        onClose={() => {
          setShowAttendanceModal(false);
          setSelectedSession(null);
          refreshAllData();
          if (attendanceModalOpenedFrom === 'history') {
            setTimeout(() => setShowAttendanceHistory(true), 100);
          }
          setAttendanceModalOpenedFrom(null);
        }}
        session={selectedSession}
        facultyId={facultyData?.facultyId || facultyData?._id || localStorage.getItem('userId')}
        policy={policy}
        onSuccess={refreshAllData}
      />

      {/* Attendance History Modal */}
      <AttendanceHistoryModal
        isOpen={showAttendanceHistory}
        onClose={() => setShowAttendanceHistory(false)}
        facultyId={facultyData?.facultyId || localStorage.getItem('userId')}
        onEditAttendance={async (session) => {
          setAttendanceModalOpenedFrom('history');
          await handleEditAttendance(session);
        }}
        onMarkAttendance={async (session) => {
          setAttendanceModalOpenedFrom('history');
          await handleEditAttendance(session);
        }}
      />
    </div>
  );
};

export default FacultyDashboard;